import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import {
  IRatioReportSettings,
  ISpecialReportOptions,
} from '../../../providers/settings/reports/ratioReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';
import { IRange } from '../../../types';

/**
 * @description saves the template as the selected template and additional settings
 * @param template
 */
export const saveRatioSettings = async (
  template: ISettingsTemplate<IRatioReportSettings>,
  timeframe: IRange,
  filter: boolean,
  tags: { [key: string]: ISpecialReportOptions }
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/ratioReport`
  );

  await setDoc(docRef, {
    selected: template ?? null,
    timeframe: timeframe ?? null,
    filter,
    ...(tags ?? null),
  });
};

/**
 * @description updates the ratio template
 * @param template
 * @param isSelected
 */
export const updateRatioTemplate = async (
  template: ISettingsTemplate<IRatioReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/ratioReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/ratioReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/ratioReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IRatioReportSettings>,
  replacement: ISettingsTemplate<IRatioReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/ratioReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<IRatioReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as Partial<unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/ratioReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};

/**
 * @description creates a new ratio report settings template
 * @param template
 */
export const createRatioTemplate = async (
  template: Partial<ISettingsTemplate<IRatioReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/ratioReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

export const createDefaultTemplates = async (): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);

  for (const templateData of defaultTemplates) {
    const docRef = doc(
      user.services.firestore,
      `userProfiles/${user.id}/settings/ratioReport/templates/${templateData.id}`
    );
    batch.set(docRef, templateData);
  }

  await batch.commit();
};

const defaultTemplates = [
  {
    active: true,
    id: 'OrDji4OaMC2pwevSXEQO',
    hawkinCreated: true,
    description: '',
    settings: {
      metricTwo: {
        metric: {
          value: 'jumpHeight',
          entity: {
            direction: 1,
            isExcludable: false,
            label: 'Jump Height',
            order: 4,
            description:
              'The change in system center of mass position between the instant of take-off and peak positive vertical displacement of the system center of mass, calculated using the vertical velocity of the system center of mass at the instant of take-off and the equations of uniformly accelerated motion.',
            roundingPrecision: 2,
            category: 'Displacement',
            units: 'm',
            id: 'jumpHeight',
          },
          label: 'Jump Height',
        },
        testType: {
          entity: {
            velocity: true,
            name: 'Squat Jump',
            defaultKey: 'jumpHeight',
            id: 'QEG7m7DhYsD6BrcQ8pic',
            abbr: 'Squat Jump',
          },
          value: 'QEG7m7DhYsD6BrcQ8pic',
          label: 'Squat Jump',
        },
        tags: [],
      },
      metricOne: {
        testType: {
          label: 'Countermovement Jump',
          entity: {
            name: 'Countermovement Jump',
            defaultKey: 'jumpHeight',
            abbr: 'CMJ',
            id: '7nNduHeM5zETPjHxvm7s',
            velocity: true,
          },
          value: '7nNduHeM5zETPjHxvm7s',
        },
        metric: {
          value: 'jumpHeight',
          label: 'Jump Height',
          entity: {
            units: 'm',
            order: 4,
            category: 'Displacement',
            label: 'Jump Height',
            direction: 1,
            id: 'jumpHeight',
            isExcludable: false,
            description:
              'The change in system center of mass position between the instant of take-off and peak positive vertical displacement of the system center of mass, calculated using the vertical velocity of the system center of mass at the instant of take-off and the equations of uniformly accelerated motion.',
            roundingPrecision: 2,
          },
        },
        tags: [],
      },
    },
    name: 'Eccentric Utilization Ratio',
  },
  {
    active: true,
    id: 'msXZOTAn3OP0qqr6DkEJ',
    description: '',
    settings: {
      metricTwo: {
        tags: [],
        metric: {
          label: 'Peak Force',
          entity: {
            roundingPrecision: 0,
            units: 'N',
            label: 'Peak Force',
            description:
              'The peak instantaneous vertical ground reaction force applied during the isometric test.',
            category: 'Force',
            order: 10,
            isExcludable: false,
            direction: 1,
            id: 'peakForce',
          },
          value: 'peakForce',
        },
        testType: {
          value: '2uS5XD5kXmWgIZ5HhQ3A',
          entity: {
            name: 'Isometric Test',
            defaultKey: 'peakForce',
            id: '2uS5XD5kXmWgIZ5HhQ3A',
            velocity: true,
            abbr: 'Iso Test',
          },
          label: 'Isometric Test',
        },
      },
      metricOne: {
        tags: [],
        testType: {
          entity: {
            name: 'Countermovement Jump',
            defaultKey: 'jumpHeight',
            abbr: 'CMJ',
            velocity: true,
            id: '7nNduHeM5zETPjHxvm7s',
          },
          value: '7nNduHeM5zETPjHxvm7s',
          label: 'Countermovement Jump',
        },
        metric: {
          value: 'peakBrakingForce',
          entity: {
            label: 'Peak Force',
            direction: 1,
            category: 'Force',
            order: 28,
            roundingPrecision: 0,
            description:
              'The peak instantaneous vertical ground reaction force applied to the system center of mass during the braking phase.',
            id: 'peakBrakingForce',
            isExcludable: true,
            units: 'N',
          },
          label: 'Peak Force',
        },
      },
    },
    hawkinCreated: true,
    name: 'Dynamic Strength Index',
    shared: true,
  },
];
